import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";

type Slide = { image: string; title: string; subtitle: string; cta: string };

type ProductBoxProps = {
  images: string[];
};

/** ProductBox with limited image count per cycle */
const ProductBox: React.FC<ProductBoxProps> = ({ images }) => {
  const [idx, setIdx] = useState(0);
  const current = encodeURI(images[idx] || images[0]);

  // autoplay every 3s
  useEffect(() => {
    const timer = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [images.length]);

  // Slide animation
  const variants = {
    initial: { opacity: 0, x: 100 },   // new image enters from right
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -100 },     // old image exits left
  };

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white p-4 md:p-6">
      <div className="bg-white overflow-hidden flex items-center justify-center h-[400px] relative">
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            src={current}
            alt="Product"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="absolute max-h-[95%] w-auto object-contain"
          />
        </AnimatePresence>
      </div>
    </div>
  );
};


const Home: React.FC = () => {
  const [activeProduct, setActiveProduct] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cycle, setCycle] = useState(1);

  // Hero slides
  const slides: Slide[] = [
    {
      image: "/pexels-photo-1.jpeg",
      title: "Premium Quality Shirts",
      subtitle: "Crafted with Excellence Since 1993",
      cta: "Shop Now",
    },
    {
      image: "/pexels-photo-2.webp",
      title: "New Fashion Collection",
      subtitle: "Modern Professional Style",
      cta: "Explore Collection",
    },
  ];

  // Change hero slide every 5s
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Product sets
  const product1 = useMemo(
    () => [
      "/wetransfer_gani-shirt-collection/GG-08 B.jpg",
      "/wetransfer_gani-shirt-collection/GG-08 A.jpg",
      "/wetransfer_gani-shirt-collection/GG-08 E.jpg",
      "/wetransfer_gani-shirt-collection/GG-08 C.jpg",
      "/wetransfer_gani-shirt-collection/GG-08 D.jpg",
    ],
    []
  );

  const product2 = useMemo(
    () => [
      "/wetransfer_gani-shirt-collection/GG-01 A.jpg",
      "/wetransfer_gani-shirt-collection/GG-01 E.jpg",
      "/wetransfer_gani-shirt-collection/GG-01 D.jpg",
      "/wetransfer_gani-shirt-collection/GG-01 B.jpg",
      "/wetransfer_gani-shirt-collection/GG-01 C.jpg",
    ],
    []
  );

  const product3 = useMemo(
    () => [
      "/wetransfer_gani-shirt-collection/GG-03 A.jpg",
      "/wetransfer_gani-shirt-collection/GG-03 B.jpg",
      "/wetransfer_gani-shirt-collection/GG-03 C.jpg",
      "/wetransfer_gani-shirt-collection/GG-03 D.jpg",
      "/wetransfer_gani-shirt-collection/GG-03 E.jpg",
    ],
    []
  );

  // Compute image limit per cycle (Cycle1=2, Cycle2=3, etc.)
  const getLimit = () => Math.min(cycle + 1, product1.length);

  // Handle when one product finishes
  const handleDone = () => {
    if (activeProduct < 2) {
      setActiveProduct((prev) => prev + 1); // move to next product
    } else {
      setActiveProduct(0); // back to first product
      setCycle((prev) => prev + 1); // next cycle
    }
  };

  const ProductCard = ({ image }: { image: string }) => (
  <div className="product-card shadow-md rounded-xl overflow-hidden hover:shadow-lg transition-transform duration-300 hover:scale-105">
    <img
      src={image}
      alt="New Collection"
      className="w-full h-[480px] object-cover"
    />
  </div>
);


  return (
    <div>
      {/* Hero Slider */}
      <section className="relative h-[65vh] md:h-[70vh] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            />
            <div className="absolute inset-0 bg-black/50" />
            <div className="relative container mx-auto px-4 h-full flex items-center">
              <div className="max-w-2xl text-white">
                <p className="text-base md:text-lg mb-3 md:mb-4 text-yellow-400 font-medium">
                  {slide.subtitle}
                </p>
                <h1 className="text-4xl font-bold mb-6 md:mb-8 leading-tight">
                  {slide.title}
                </h1>
                <Link
                  to="/collection"
                  className="inline-flex items-center gap-2 bg-yellow-600 text-white px-7 py-3 rounded-lg hover:bg-yellow-700 transition-all duration-300 font-semibold text-base hover:scale-105"
                >
                  <ShoppingBag className="w-5 h-5" />
                  {slide.cta}
                </Link>
              </div>
            </div>
          </div>
        ))}

        {/* Hero dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                i === currentSlide
                  ? "bg-yellow-500 scale-125"
                  : "bg-white/50 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-gray-50">
  <div className="container mx-auto px-4">
    <div className="text-center mb-6 md:mb-8">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        Why Choose GANI Shirts?
      </h2>
      <p className="text-[14px] md:text-base text-gray-600 max-w-2xl mx-auto">
        Experience the perfect blend of traditional craftsmanship and modern style
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {/* Card 1 */}
      <motion.div
        className="text-center group bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-transform"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        viewport={{ amount: 0.2, once: true }}
      >
        <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-red-200 transition-colors">
          <Star className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Premium Quality</h3>
        <p className="text-[14px] md:text-base text-center text-gray-600">
          Crafted from the finest Italian, Egyptian, and Turkish fabrics with exceptional attention to detail.
        </p>
      </motion.div>

      {/* Card 2 */}
      <motion.div
        className="text-center group bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-transform"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
        viewport={{ amount: 0.2, once: true }}
      >
        <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-green-200 transition-colors">
          <ShoppingBag className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Perfect Fit</h3>
        <p className="text-[14px] md:text-base text-center text-gray-600">
          Made-to-measure styling ensures every shirt fits perfectly and enhances your professional appearance.
        </p>
      </motion.div>

      {/* Card 3 */}
      <motion.div
        className="text-center group bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-transform"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut", delay: 0.4 }}
        viewport={{ amount: 0.2, once: true }}
      >
        <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 transition-colors">
          <Star className="w-8 h-8 text-blue-800" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Since 1993</h3>
        <p className="text-[14px] md:text-base text-center text-gray-600">
          Over 30 years of expertise in creating the world's finest shirts for professionals worldwide.
        </p>
      </motion.div>
    </div>
  </div>
</section>


      {/* Featured Products 
<section className="py-12">
  <div className="container mx-auto px-4">
    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
      Featured Collection
    </h2>
    <p className="text-[14px] md:text-base text-center text-gray-600 mb-8">
      Crafted for elegance and everyday wear
    </p>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
      <ProductBox images={product1} />
      <ProductBox images={product2} />
      <ProductBox images={product3} />
    </div>

   
    <div className="text-center">
      <Link
        to="/collection"
        className="inline-flex items-center gap-2 bg-yellow-600 text-white px-8 py-4 rounded-lg hover:bg-yellow-700 transition-all duration-300 font-semibold text-base hover:scale-105"
      >
        View All Collections
      </Link>
    </div>
  </div>
</section> */}

<section className="new-collection-section py-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Title */}
        <h2 className="new-collection-title text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
          Featured Collection
        </h2>
        <p className="new-collection-subtitle text-sm md:text-base text-gray-600 text-center mb-10">
          Crafted for elegance and everyday wear
        </p>

        {/* Product Grid */}
        <div className="new-collection-grid grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
          <ProductCard image="/new-collection/16.png" />
          <ProductCard image="/new-collection/5.png" />
          <ProductCard image="/new-collection/64.png" />
        </div>

        {/* View All Button */}
        <div className="new-collection-button text-center">
          <Link
             to="/newcollection"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-600 text-white font-semibold text-base rounded-lg hover:bg-yellow-700 transition-all duration-300 hover:scale-105"
          >
            View All Collections
          </Link>
        </div>
      </div>
    </section>


      {/* CTA */}
      <section className="relative py-12 bg-gray-900 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('/pexels-photo-1.jpeg')" }}
        />

        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false, amount: 0.2 }}   // 👈 replay always
          >
            Experience Premium Quality
          </motion.h2>

          <motion.p
            className="text-[14px] md:text-base text-gray-300 mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: false, amount: 0.2 }}
          >
            Join thousands of satisfied customers who trust GANI Shirts for their professional wardrobe
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: false, amount: 0.2 }}
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-yellow-600 text-white px-8 py-4 rounded-lg hover:bg-yellow-700 transition-all duration-300 font-semibold text-base hover:scale-105"
            >
              Get In Touch
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Home;